'use client';
import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { useCustomer } from '@hooks/customer';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { FileUpload, Input, PhoneInput } from '@repo/ui/src/components/Form';
import { useUpdateCustomer } from '@hooks/customer';
import { classNames } from '@repo/ui/src/utils/classNames';
import { accountUpdated } from '@repo/ui/src/services/analytics';
import isEqual from 'lodash/isEqual';
import '@repo/ui/src/utils/phoneValidation';
import LoadingLogo from '@components/Loading/LoadingLogo';

export interface Input {
  label?: string;
  placeholder?: string;
  errorText?: string;
}
interface Props {
  title?: string;
  saveButtonText?: string;
  cancelButtonText?: string;
  editButtonText?: string;
  firstNameInput?: Input;
  lastNameInput?: Input;
  phoneInput?: Input;
  emailInput?: Input;
  photoUpload?: string;
  deletePhoto?: string;
}

const AccountProfile = ({
  firstNameInput,
  lastNameInput,
  phoneInput,
  photoUpload = 'Upload Photo',
  emailInput,
  saveButtonText = 'Save Information',
  cancelButtonText = 'Cancel',
  editButtonText = 'Edit Information',
  deletePhoto = 'Delete Photo',
  title,
}: Props) => {
  const { data: user } = useCustomer();
  const { trigger } = useUpdateCustomer();
  const [isEditing, setIsEditing] = useState(false);
  const inputStyles = classNames(!isEditing ? 'bg-transparent-light-300' : 'bg-neutral-100');

  const handleSubmit = async (values: any, actions: any) => {
    if (user && !isEqual(values, user)) {
      const result = await trigger(values);
      accountUpdated({
        first_name: values.firstName,
        last_name: values.lastName,
        phone_number: values?.phone,
        email: values?.email,
        // @ts-expect-error ts doesn't know how to filter boolean
        updated_fields: Object.entries(result ?? values)
          .map(([key, value]) => {
            if (value !== user![key as keyof typeof user]) {
              return {
                field_name: key,
                field_value: value,
                update_action: user![key as keyof typeof user] ? 'Update' : 'Add',
              };
            }
          })
          .filter(Boolean),
      });
      setIsEditing(false);
      return actions.resetForm({
        values: { firstName: result.firstName, lastName: result.lastName, avatar: result.avatar },
      });
    }
    setIsEditing(false);
    actions.resetForm({ values });
  };

  const validationSchema = () =>
    Yup.object().shape({
      firstName: Yup.string().required('First name is required.'),
      lastName: Yup.string().required('Last name is required.'),
      phone: Yup.string().phone('Phone number must be valid'),
    });

  return (
    <div>
      {title && (
        <h4 className="tra-text-web-heading-4 mb-5" tabIndex={0}>
          {title}
        </h4>
      )}
      <div className="bg-warm-100 border border-warm-300 px-4 py-5 sm:p-10">
        <Formik
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          initialValues={{
            firstName: user?.firstName,
            lastName: user?.lastName,
            phone: user?.phone ?? '',
            avatar: user?.avatar,
          }}
        >
          {({ isSubmitting, handleReset, dirty, values }) => (
            <Form className="flex flex-col sm:gap-y-7 md:flex-row md:gap-16">
              <div className="flex justify-start mb-5">
                <FileUpload
                  id="avatar"
                  name="avatar"
                  accept="image/*"
                  src={values?.avatar}
                  photoUpload={photoUpload}
                  isEditing={isEditing}
                  deletePhoto={deletePhoto}
                />
              </div>
              <div>
                <div className="flex flex-col gap-y-5 sm:gap-y-7 items-center md:flex-row md:gap-x-6 mb-5">
                  <Input
                    id="firstName"
                    name="firstName"
                    label={firstNameInput?.label}
                    disabled={!isEditing}
                    inputStyles={inputStyles}
                  />
                  <Input
                    id="lastName"
                    name="lastName"
                    label={lastNameInput?.label}
                    disabled={!isEditing}
                    inputStyles={inputStyles}
                  />
                </div>

                <div className="flex justify-center flex-col">
                  <label className="flex flex-col w-full tra-text-web-body-s relative mb-5">
                    {emailInput?.label}
                    <input
                      className="tra-input-text tra-text-web-body-m bg-transparent-light-300"
                      disabled
                      value={user?.email}
                    />
                  </label>
                </div>
                <PhoneInput
                  id="phone"
                  name="phone"
                  label={phoneInput?.label}
                  disabled={!isEditing}
                  inputStyles={inputStyles}
                />
                <div className="flex flex-col sm:flex-row justify-start pt-5 gap-6">
                  {!isEditing ? (
                    <div>
                      <Button variant={ButtonVariant.secondary} onClick={() => setIsEditing(true)}>
                        {editButtonText}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button
                        type="submit"
                        variant={ButtonVariant.secondary}
                        disabled={isSubmitting || !dirty}
                        className="w-full sm:w-fit"
                      >
                        {isSubmitting ? <LoadingLogo small /> : `${saveButtonText}`}
                      </Button>
                      <div>
                        <Button
                          variant={ButtonVariant.secondary}
                          onClick={() => {
                            handleReset();
                            setIsEditing(false);
                          }}
                          disabled={isSubmitting}
                          className="w-full sm:w-fit"
                        >
                          {cancelButtonText}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AccountProfile;
